/**
 * @bos/portal3 — the APPS section of a project page: what this business is having built.
 *
 * The owner's ruling made real on screen — "one project can have more than one app". A
 * business page used to have one build box and an undifferentiated pile of runs under it;
 * now it names apps, and each app is its own thing with its own page, its own builds and
 * its own download.
 *
 * NAMING HAPPENS AT BUILD TIME, beside the description: one form, two questions, one
 * press. Under the hood that is two server calls — name the app, then build it — and when
 * the second one is refused the first is NOT undone: the app really was named, it is in
 * the list below, and the message says so and points at it. Silently deleting something a
 * person just named would be the dishonest half of a "cleaner" flow.
 *
 * THE LIVE-RUN GATE spans the whole business (one build at a time), so it is shown here
 * with the same words the app page uses, naming whichever app is holding the machine.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Run } from '@bos/portal3-server';
import { PATHS } from '../routes';
import { client } from '../rpc/client';
import {
  runErrorMessage,
  startConflictOf,
  useProjectRuns,
  type StartConflict,
} from '../runs/hooks';
import { LiveRunNotice, statusBadgeClass } from '../runs/StartRunSection';
import { isTerminalStatus, statusLabel } from '../runs/model';
import { appErrorMessage, appNameConflictOf, useProjectApps, type App } from './hooks';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** One app in the list: what it is called, how it is doing, and the way in. */
function AppRow({ app, runs }: { app: App; runs: readonly Run[] }) {
  const latest = runs[0];
  return (
    <li className="app-row">
      <div className="app-row__head">
        <Link className="app-row__name" to={PATHS.app(app.id)}>
          {app.name}
        </Link>
        {latest !== undefined && (
          <span className={`badge ${statusBadgeClass(latest.status)}`}>
            {statusLabel(latest.status)}
          </span>
        )}
      </div>
      <span className="app-row__meta">
        {runs.length === 0
          ? `named ${formatDate(app.createdAt)} · not built yet`
          : `${runs.length} build${runs.length === 1 ? '' : 's'} · downloads as ${app.slug}.zip`}
      </span>
      <Link className="btn btn--ghost btn--sm app-row__open" to={PATHS.app(app.id)}>
        Open
      </Link>
    </li>
  );
}

export function AppsSection({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { apps, status: appsStatus, reload: reloadApps, create } = useProjectApps(projectId);
  const { runs, reload: reloadRuns } = useProjectRuns(projectId);

  const [name, setName] = useState('');
  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<StartConflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [takenAppId, setTakenAppId] = useState<string | null>(null);
  /** Named, but the build did not start. The app is real and stays real — say where it is. */
  const [namedNotBuilt, setNamedNotBuilt] = useState<App | null>(null);

  const liveRun = runs.find((r) => !isTerminalStatus(r.status));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setConflict(null);
    setTakenAppId(null);
    setNamedNotBuilt(null);
    setBusy(true);
    let named: App;
    try {
      named = await create(name);
    } catch (err) {
      const taken = appNameConflictOf(err);
      if (taken !== null) {
        setTakenAppId(taken.appId);
        setError(taken.message);
      } else {
        setError(appErrorMessage(err));
      }
      setBusy(false);
      return;
    }
    try {
      // Byte-for-byte: the textarea's exact content is the task the factory hashes on.
      const run = await client.runs.start.mutate({ appId: named.id, projectTask: task });
      navigate(PATHS.run(run.id));
    } catch (err) {
      // The app is NAMED and stays named — say so, and point at it.
      const asConflict = startConflictOf(err);
      if (asConflict !== null) setConflict(asConflict);
      else setError(runErrorMessage(err));
      setNamedNotBuilt(named);
      void reloadApps();
      void reloadRuns();
      setBusy(false);
    }
  }

  const runsOf = (appId: string): Run[] => runs.filter((r) => r.appId === appId);
  // Runs from before apps existed belong to no app. They are not hidden and they are not
  // adopted by one either — the honest place for them is their own line.
  const appless = runs.filter((r) => r.appId === null);

  return (
    <section className="card apps" aria-label="Apps">
      <h2 className="card__title">Build an app</h2>
      <p className="card__intro">
        Your business can have as many apps as it needs — a counter till, a booking board, a
        stock book. Name this one and say what it should do.
      </p>

      <LiveRunNotice conflict={conflict} liveRun={liveRun} />

      {namedNotBuilt !== null && (
        <p className="card__intro" role="status">
          “{namedNotBuilt.name}” is saved as an app of this business — nothing was lost.{' '}
          <Link to={PATHS.app(namedNotBuilt.id)}>Open it</Link> to build it when the machine
          is free.
        </p>
      )}

      <form className="form" onSubmit={(e) => void onSubmit(e)} aria-label="Name and build an app">
        {error && (
          <p className="form__error" role="alert">
            {error}
            {takenAppId !== null && (
              <>
                {' '}
                <Link to={PATHS.app(takenAppId)}>Open it</Link>.
              </>
            )}
          </p>
        )}
        <label className="form__field">
          <span className="form__label">What are you calling it?</span>
          <input
            className="form__input"
            type="text"
            name="appName"
            maxLength={64}
            placeholder="e.g. Counter till"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="form__field">
          <span className="form__label">What should it do?</span>
          <textarea
            className="form__textarea build-start__task"
            name="projectTask"
            placeholder="e.g. A point-of-sale for the bakery: products, daily stock, sales at the counter, end-of-day waste write-off…"
            value={task}
            onChange={(e) => setTask(e.target.value)}
            disabled={busy}
            rows={5}
          />
        </label>
        <div className="form__actions">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy || name.trim() === '' || task.trim() === '' || liveRun !== undefined}
          >
            {busy ? 'Starting…' : 'Build'}
          </button>
        </div>
      </form>

      {appsStatus === 'ready' && apps.length > 0 && (
        <div className="build-start__history">
          <h3 className="build-start__history-title">Your apps</h3>
          <ul className="app-list">
            {apps.map((app) => (
              <AppRow key={app.id} app={app} runs={runsOf(app.id)} />
            ))}
          </ul>
        </div>
      )}

      {appless.length > 0 && (
        <div className="build-start__history">
          <h3 className="build-start__history-title">Earlier builds</h3>
          <p className="card__intro">
            {appless.length === 1 ? 'This build was' : 'These builds were'} made before apps had
            names of their own, so {appless.length === 1 ? 'it belongs' : 'they belong'} to this
            business rather than to a named app.
          </p>
          <ul className="run-list">
            {appless.map((run) => (
              <li className="run-row" key={run.id}>
                <span className={`badge ${statusBadgeClass(run.status)}`}>
                  {statusLabel(run.status)}
                </span>
                <span className="run-row__meta">started {formatDate(run.startedAt)}</span>
                <Link className="btn btn--ghost btn--sm run-row__open" to={PATHS.run(run.id)}>
                  Open
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
